import React from "react";
import {
  Checkbox,
  Icon,
  Message,
  MessageHeader,
  MessageItem,
  MessageList,
  Table,
} from "semantic-ui-react";
import styled from "styled-components";
import { DuplicateSignalInfo } from "../../../../util/dbcParser";

export const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
`;

export const InfoNote = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9em;
`;

interface DuplicateSignalsWarningProps {
  duplicates: DuplicateSignalInfo[];
  allowDuplicates: boolean;
  onAllowDuplicatesChange: (allowed: boolean) => void;
  shouldShowAllowDuplicatesCheckbox?: boolean;
}

export default function DuplicateSignalsWarning({
  duplicates,
  allowDuplicates,
  onAllowDuplicatesChange,
  shouldShowAllowDuplicatesCheckbox = true,
}: DuplicateSignalsWarningProps) {
  if (duplicates.length === 0) {
    return null;
  }

  const totalDuplicates = duplicates.reduce(
    (sum, dup) => sum + dup.appearsIn.length,
    0
  );
  const uniqueDuplicates = duplicates.length;

  return (
    <Container>
      <Message warning style={{ marginBottom: "0px" }}>
        <MessageHeader>
          <Icon name="warning" />
          Duplicate Signal Names Detected
        </MessageHeader>
        <MessageList>
          <MessageItem>
            Found <strong>{uniqueDuplicates}</strong> signal
            {uniqueDuplicates !== 1 ? "s" : ""} with duplicate names across{" "}
            <strong>{totalDuplicates}</strong> occurrence
            {totalDuplicates !== 1 ? "s" : ""}:
          </MessageItem>
        </MessageList>
      </Message>

      <Table celled compact size="small">
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Signal Name</Table.HeaderCell>
            <Table.HeaderCell>CAN IDs</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {duplicates.map((duplicate, index) => {
            const canIds = duplicate.appearsIn.map((msg) => msg.id).join(", ");
            return (
              <Table.Row key={index}>
                <Table.Cell>
                  <strong>{duplicate.signalName}</strong>
                </Table.Cell>
                <Table.Cell>{canIds}</Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>

      {shouldShowAllowDuplicatesCheckbox && (
        <>
          <CheckboxLabel>
            <Checkbox
              checked={allowDuplicates}
              style={{ marginRight: "8px" }}
              disabled={false}
              onChange={(e, { checked }) =>
                onAllowDuplicatesChange(checked ?? false)
              }
            />
            <span>
              <strong>Allow duplicate signal names:</strong> Proceed with DBC
              creation despite duplicate signals
            </span>
          </CheckboxLabel>
          {allowDuplicates && (
            <InfoNote>
              <Icon name="info circle" />
              Note: When duplicate signals are allowed, the system will
              automatically append the CAN ID as a suffix to each signal name to
              ensure uniqueness and prevent conflicts during DBC processing.
            </InfoNote>
          )}
        </>
      )}
    </Container>
  );
}
